/*
 * Cart freebie: keeps free gifts in the cart while the subtotal (excluding the
 * gifts) is at or above each gift's threshold, and removes them when it drops below.
 * With several gifts (tiers), only the highest qualified gift is kept unless config.keepLower is set.
 * Config comes from window.cartFreebie (see layout/theme.liquid).
 *
 * Cart requests made by the theme are intercepted: if the freebie needs to change,
 * the fix is applied before the theme re-renders, so the cart updates in one pass.
 * Checkout is held until any in-flight cart request has finished and the cart is verified.
 */
(() => {
  const config = window.cartFreebie;
  if (!config) return;

  const nativeFetch = window.fetch.bind(window);
  const stripQuery = (url) => url.split('?')[0];
  const addUrl = stripQuery(window.routes.cart_add_url);
  const changeUrls = [window.routes.cart_change_url, window.routes.cart_update_url].map(stripQuery);
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  const giftProductIds = config.gifts.map((gift) => gift.productId);
  const isFreebie = (item) => giftProductIds.includes(item.product_id);
  const getCart = async () => (await nativeFetch('/cart.js', { headers: { Accept: 'application/json' } })).json();

  // Returns the `updates` needed to reach the desired gift state, or null if already correct.
  const getUpdates = (cart) => {
    const current = cart.items.filter(isFreebie);
    const subtotal = cart.items.filter((item) => !isFreebie(item)).reduce((sum, item) => sum + item.final_line_price, 0);
    let qualified = config.gifts.filter((gift) => gift.available && subtotal > 0 && subtotal >= gift.threshold);
    if (!config.keepLower && qualified.length > 1) {
      const top = Math.max(...qualified.map((gift) => gift.threshold));
      qualified = qualified.filter((gift) => gift.threshold === top).slice(0, 1);
    }
    const desired = qualified.map((gift) => gift.variantId);

    const correct = desired.every((variantId) => current.some((item) => item.variant_id === variantId && item.quantity === 1))
      && current.every((item) => desired.includes(item.variant_id) && item.quantity === 1);
    if (correct) return null;

    const updates = {};
    current.forEach((item) => { updates[item.variant_id] = 0; });
    desired.forEach((variantId) => { updates[variantId] = 1; });
    return updates;
  };

  const getRequestSections = (init) => {
    const body = init && init.body;
    try {
      if (body instanceof FormData) return { sections: body.get('sections'), sections_url: body.get('sections_url') };
      if (typeof body === 'string') {
        const { sections, sections_url } = JSON.parse(body);
        return { sections, sections_url };
      }
    } catch (e) {}
    return {};
  };

  // Applies the freebie fix in a single request; returns the new cart (with re-rendered sections if asked for).
  const applyUpdates = async (updates, requested) => {
    const payload = { updates };
    if (requested.sections) {
      payload.sections = requested.sections;
      payload.sections_url = requested.sections_url || window.location.pathname;
    }
    // Ajax API endpoint: it returns the cart as JSON and renders the requested sections.
    const response = await nativeFetch(`${stripQuery(window.routes.cart_update_url)}.js`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(payload)
    });
    return response.ok ? response.json() : null;
  };

  const jsonResponse = (original, data) => new Response(JSON.stringify(data), {
    status: original.status,
    statusText: original.statusText,
    headers: { 'Content-Type': 'application/json' }
  });

  let inFlight = 0;
  const idleWaiters = [];
  const whenIdle = () => (inFlight === 0 ? Promise.resolve() : new Promise((resolve) => idleWaiters.push(resolve)));

  const handle = async (response, input, init) => {
    if (!response.ok) return response;
    const url = stripQuery(typeof input === 'string' ? input : input.url || '');
    const isAdd = url.endsWith(addUrl);
    const isChange = changeUrls.some((u) => url.endsWith(u));
    if (!isAdd && !isChange) return response;

    const original = await response.clone().json();
    // change/update return the whole cart; add returns only the added item.
    const cart = isAdd ? await getCart() : original;
    const updates = getUpdates(cart);
    if (!updates) return response;

    const fixed = await applyUpdates(updates, getRequestSections(init));
    if (!fixed) return response;
    // Keep the shape the caller expects: the added item for add, the cart otherwise.
    return jsonResponse(response, isAdd ? { ...original, sections: fixed.sections } : fixed);
  };

  window.fetch = async (input, init) => {
    const url = stripQuery(typeof input === 'string' ? input : input.url || '');
    const isCartRequest = [addUrl, ...changeUrls].some((u) => url.endsWith(u));
    if (!isCartRequest) return nativeFetch(input, init);

    inFlight++;
    try {
      const response = await nativeFetch(input, init);
      try {
        return await handle(response, input, init);
      } catch (e) {
        return response;
      }
    } finally {
      if (--inFlight === 0) idleWaiters.splice(0).forEach((resolve) => resolve());
    }
  };

  // Hold checkout until pending cart requests finish and the freebie state is verified.
  let checkingOut = false;
  const guardCheckout = async (event) => {
    const trigger = event.target.closest && event.target.closest('[name="checkout"], form[action$="/cart"] [type="submit"][name="checkout"]');
    if (!trigger || checkingOut) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    checkingOut = true;
    try {
      await whenIdle();
      const updates = getUpdates(await getCart());
      if (updates) await applyUpdates(updates, {});
    } catch (e) {}
    window.location.href = '/checkout';
  };
  document.addEventListener('click', guardCheckout, true);

  // "Buy it now" (Shopify dynamic checkout) skips the cart entirely, so the freebie would never be added.
  // When the item alone reaches a gift threshold, send it through the cart (where the freebie is applied)
  // and then to checkout. Below the threshold, or on any error, the native button is left alone.
  let buyingNow = false;
  const SPINNER = '<svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" style="width:2.2rem;height:2.2rem"><circle class="path" fill="none" style="stroke:#fff !important" stroke-width="6" cx="33" cy="33" r="30"></circle></svg>';
  const setBuyNowLoading = (button, on) => {
    if (!button) return;
    let overlay = button.querySelector('.freebie-buynow-spinner');
    if (on && !overlay) {
      overlay = document.createElement('span');
      overlay.className = 'freebie-buynow-spinner';
      overlay.style.cssText = 'position:absolute;inset:0;display:flex;align-items:center;justify-content:center;pointer-events:none;';
      overlay.innerHTML = SPINNER;
      button.appendChild(overlay);
    }
    if (overlay) overlay.hidden = !on;
    button.style.position = 'relative';
    button.style.color = on ? 'transparent' : '';
    button.style.pointerEvents = on ? 'none' : '';
  };
  const guardBuyNow = async (event) => {
    const trigger = event.target.closest && event.target.closest('.shopify-payment-button');
    if (!trigger || buyingNow) return;
    const form = trigger.closest('form');
    const variantInput = form && form.querySelector('[name="id"]');
    const handle = (window.location.pathname.match(/\/products\/([^/?#]+)/) || [])[1];
    if (!variantInput || !handle) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    buyingNow = true;
    const nativeButton = trigger.querySelector('button, [role="button"]');
    setBuyNowLoading(nativeButton, true);
    const variantId = Number(variantInput.value);
    const quantity = Math.max(1, parseInt((form.querySelector('[name="quantity"]') || {}).value, 10) || 1);
    try {
      const product = await (await nativeFetch(`/products/${handle}.js`, { headers: { Accept: 'application/json' } })).json();
      const variant = product.variants.find((v) => v.id === variantId);
      if (!variant || giftProductIds.includes(product.id) || !config.gifts.some((gift) => gift.available && variant.price * quantity >= gift.threshold)) {
        throw new Error('freebie not needed');
      }
      // Goes through the patched fetch above, so the freebie is added alongside the item.
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify({ items: [{ id: variantId, quantity }] })
      });
      if (!response.ok) throw new Error('add failed');
      const updates = getUpdates(await getCart());
      if (updates) await applyUpdates(updates, {});
      window.location.href = '/checkout';
    } catch (e) {
      // Fall back to the native Buy it now behaviour.
      setBuyNowLoading(nativeButton, false);
      nativeButton?.click();
      setTimeout(() => { buyingNow = false; }, 1000);
    }
  };
  document.addEventListener('click', guardBuyNow, true);

  // Page load: catch carts that changed outside the theme (permalinks, returning from checkout).
  (async () => {
    try {
      const updates = getUpdates(await getCart());
      if (updates && await applyUpdates(updates, {})) window.location.reload();
    } catch (e) {}
  })();
})();
